import { useCallback, useEffect, useRef, useState } from 'react';
import { archiveByKey, type Archive } from '../api/archives';
import {
  annotationItemId,
  findAnnotations,
  findItems,
  stripHtml,
  type PandoraAnnotation,
  type PandoraItem,
} from '../api/pandora';
import { posterUrl } from '../engine/mediaurls';
import { formatTime } from '../format/helpers';
import { saveToBin } from '../state/bin';
import ItemDetail, { type ItemPreset } from './ItemDetail';
import './SearchPanel.css';

const PAGE = 20;

type Mode = 'items' | 'moments';

interface Props {
  archives: Archive[];
  onOpenAdvanced?: () => void;
}

export default function SearchPanel({ archives, onOpenAdvanced }: Props) {
  const [archiveKey, setArchiveKey] = useState(archives[0]?.key ?? 'pad.ma');
  const [mode, setMode] = useState<Mode>('items');
  const [query, setQuery] = useState('');
  const [streamOnly, setStreamOnly] = useState(false);
  const [items, setItems] = useState<PandoraItem[]>([]);
  const [moments, setMoments] = useState<PandoraAnnotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ itemId: string; preset?: ItemPreset } | null>(null);
  const [binnedId, setBinnedId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const generation = useRef(0);

  const archive = archiveByKey(archives, archiveKey) ?? archives[0];

  const runSearch = useCallback(
    async (offset: number) => {
      if (!archive) return;
      const gen = ++generation.current;
      setLoading(true);
      setError(null);
      try {
        if (mode === 'items') {
          const page = await findItems(archive.site, {
            query,
            range: [offset, offset + PAGE],
            streamableOnly: streamOnly && archive.key !== 'pad.ma',
          });
          if (gen !== generation.current) return;
          setItems((prev) => (offset === 0 ? page : [...prev, ...page]));
          setExhausted(page.length < PAGE);
        } else {
          if (!query.trim()) {
            setMoments([]);
            setExhausted(true);
            return;
          }
          const page = await findAnnotations(archive.site, query, [offset, offset + PAGE]);
          if (gen !== generation.current) return;
          setMoments((prev) => (offset === 0 ? page : [...prev, ...page]));
          setExhausted(page.length < PAGE);
        }
      } catch (e) {
        if (gen !== generation.current) return;
        setError(e instanceof Error ? e.message : 'search failed');
      } finally {
        if (gen === generation.current) setLoading(false);
      }
    },
    [archive, mode, query, streamOnly],
  );

  // debounce new searches
  useEffect(() => {
    setItems([]);
    setMoments([]);
    setExhausted(false);
    const t = setTimeout(() => void runSearch(0), 350);
    return () => clearTimeout(t);
  }, [runSearch]);

  // infinite scroll
  function onScroll() {
    const el = listRef.current;
    if (!el || loading || exhausted) return;
    if (el.scrollTop + el.clientHeight > el.scrollHeight - 160) {
      void runSearch(mode === 'items' ? items.length : moments.length);
    }
  }

  if (detail && archive) {
    return (
      <div className="search-panel">
        <ItemDetail
          archive={archive}
          itemId={detail.itemId}
          preset={detail.preset}
          onBack={() => setDetail(null)}
        />
      </div>
    );
  }

  return (
    <div className="search-panel">
      <div className="search-controls">
        <select value={archiveKey} onChange={(e) => setArchiveKey(e.target.value)}>
          {archives.map((a) => (
            <option key={a.key} value={a.key}>
              {a.name}
            </option>
          ))}
        </select>
        <div className="search-modes">
          <button className={mode === 'items' ? 'active' : ''} onClick={() => setMode('items')}>
            Items
          </button>
          <button
            className={mode === 'moments' ? 'active' : ''}
            onClick={() => setMode('moments')}
          >
            Moments
          </button>
        </div>
        <input
          className="search-query"
          placeholder={mode === 'items' ? 'Search items…' : 'Search transcripts, keywords…'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {onOpenAdvanced && (
          <button
            className="ghost search-advanced"
            title="Open advanced search"
            onClick={onOpenAdvanced}
          >
            Advanced ▸
          </button>
        )}
        {archive && archive.key !== 'pad.ma' && mode === 'items' && (
          <label className="field search-streamable">
            <input
              type="checkbox"
              checked={streamOnly}
              onChange={(e) => setStreamOnly(e.target.checked)}
            />
            streamable only
          </label>
        )}
      </div>

      <div className="search-results" ref={listRef} onScroll={onScroll}>
        {error && <div className="search-error">{error}</div>}

        {mode === 'items' &&
          archive &&
          items.map((item) => (
            <div
              key={item.id}
              className="result-card"
              role="button"
              tabIndex={0}
              onClick={() => setDetail({ itemId: item.id })}
              onKeyDown={(e) => e.key === 'Enter' && setDetail({ itemId: item.id })}
            >
              <img
                className="result-poster"
                src={posterUrl(archive, item.id)}
                alt=""
                loading="lazy"
              />
              <span className="result-info">
                <span className="result-title">{item.title || item.id}</span>
                <span className="result-meta">
                  {[item.director?.join(', '), item.year].filter(Boolean).join(' · ')}
                </span>
                <span className="result-meta timecode">
                  {item.duration ? formatTime(item.duration) : '—'}
                  {item.parts > 1 ? ` · ${item.parts} parts` : ''}
                </span>
              </span>
              <button
                className="ghost result-bin"
                title="Save to bin"
                onClick={(e) => {
                  e.stopPropagation();
                  saveToBin({
                    archive: archive.key,
                    item: item.id,
                    title: item.title || item.id,
                    duration: item.duration ?? 0,
                    parts: item.parts ?? 1,
                  });
                  setBinnedId(item.id);
                  setTimeout(() => setBinnedId((id) => (id === item.id ? null : id)), 1200);
                }}
              >
                {binnedId === item.id ? '✓' : '+ Bin'}
              </button>
            </div>
          ))}

        {mode === 'moments' &&
          archive &&
          moments.map((m) => {
            const itemId = annotationItemId(m.id);
            return (
              <button
                key={m.id}
                className="result-card"
                onClick={() =>
                  setDetail({ itemId, preset: { concatIn: m.in, concatOut: m.out } })
                }
              >
                <img
                  className="result-poster"
                  src={posterUrl(archive, itemId)}
                  alt=""
                  loading="lazy"
                />
                <span className="result-info">
                  <span className="result-title">{m.title || itemId}</span>
                  <span className="result-meta timecode">
                    {formatTime(m.in)} – {formatTime(m.out)}
                  </span>
                  <span className="result-snippet">{stripHtml(m.value)}</span>
                </span>
              </button>
            );
          })}

        {mode === 'moments' && !query.trim() && !moments.length && (
          <div className="search-hint">type to search annotations (transcripts, keywords…)</div>
        )}
        {loading && <div className="search-hint">loading…</div>}
        {!loading &&
          exhausted &&
          (mode === 'items' ? items.length === 0 : moments.length === 0) &&
          query.trim() !== '' &&
          !error && <div className="search-hint">no results</div>}
      </div>
    </div>
  );
}
